import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PhinAvatarLazy } from '@belong/ai-pointer';
import { BASE_PATHS, palette, PATHS } from '@belong/common';
import { IconArrowDown } from '@belong/icons';
import { Text } from '@belong/ui';
import { MaintenanceModel } from 'api/models';
import classNames from 'classnames/bind';
import Label from 'components/Label/Label';
import { LabelItem } from 'components/Label/labelTypes';
import { MAINTENANCE_LABEL_CLASS_NAME } from 'maintenance/constants';
import styles from 'containercomponents/MaintenanceStatus/ModalMaintenanceHeader/ModalMaintenanceHeader.module.css';
import Space from 'corecomponents/Space/Space';
import { SPACE_TYPES } from 'corecomponents/Space/spaceTypes';
import ModalLayout from 'layouts/ModalLayout/ModalLayout';
import { splitCamelCase } from 'utils/strings';

const cx = classNames.bind(styles);

interface ModalMaintenanceHeaderProps {
  title: string;
  streetAddress: string;
  // Only ever interpolated into a string (`#${jobId}`, the thread link), and
  // the maintenance id it is given is numeric.
  jobId: string | number;
  propertyUniqueId: string;
  labels?: LabelItem[];
  conversationId?: string;
  unitId?: string;
  assessment: MaintenanceModel['assessment'];
  requestType: MaintenanceModel['requestType'];
}

const ModalMaintenanceHeader = ({
  title,
  streetAddress,
  jobId,
  propertyUniqueId,
  labels = [],
  conversationId,
  unitId,
  assessment,
  requestType,
}: ModalMaintenanceHeaderProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const [showEllipsis, setShowEllipsis] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (linkRef.current) {
        const element = linkRef.current;
        const isOverflowing = element.scrollWidth > element.clientWidth;
        setShowEllipsis(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, []);

  return (
    <>
      <div className="mb-sm flex justify-center">
        <Link
          to={`${BASE_PATHS.ACCOUNTS}/portfolio/properties/${propertyUniqueId}`}
          className="text-dark-gray"
        >
          {streetAddress}
        </Link>
        <span className="mx-2xs text-dark-gray">/</span>
        <Link
          ref={linkRef}
          to={`${BASE_PATHS.ACCOUNTS}/portfolio/maintenance`}
          className={cx(
            'text-dark-gray overflow-hidden max-w-xs whitespace-nowrap inline-block breadcrumb-link',
            {
              'data-overflow': showEllipsis,
            },
          )}
        >
          {showEllipsis ? '...' : 'Pro Service'}
        </Link>
        <span className="mx-2xs text-dark-gray">/</span>
        <span className="font-semibold">{`#${jobId}`}</span>
      </div>
      <ModalLayout.Title label={title} className={cx('title', 'my-0 pb-0')} />
      <Text variant="h2" className="mb-sm text-center">
        {assessment ? `${assessment} ` : ''}
        {requestType ? splitCamelCase(requestType) : requestType}
      </Text>

      {labels.length > 0 && (
        <div className={cx('label-holder')}>
          {labels.map((label, index) => (
            <Label
              text={label.text}
              color={label.color}
              className={MAINTENANCE_LABEL_CLASS_NAME}
              key={index}
            />
          ))}
        </div>
      )}

      {conversationId && (
        <div className="flex justify-center">
          <Link
            to={`${PATHS.RESIDENT_MAINTENANCE_SCHEDULE.replace(
              ':unitId',
              unitId!,
            )}?threadId=${conversationId}&from=${jobId}`}
            className="mt-sm flex items-center gap-xs"
          >
            <div className="pointer-events-none flex h-md w-md shrink-0 items-center justify-center">
              <PhinAvatarLazy size={24} idleJumps={false} />
            </div>
            <Text
              fontWeight="semibold"
              className="transition-colors duration-hover hover:text-dark-navy"
            >
              Phin Created Ticket
            </Text>
            <IconArrowDown
              className="h-checkbox w-checkbox -rotate-90"
              fill={palette.navy}
            />
          </Link>
        </div>
      )}
      <Space value={SPACE_TYPES.MD} mobileValue={SPACE_TYPES.LG} />
    </>
  );
};

export default ModalMaintenanceHeader;
